import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bug, 
  X, 
  Copy, 
  CheckCircle, 
  AlertCircle,
  ExternalLink,
  Settings
} from 'lucide-react';

interface EnvironmentDebugProps {
  isOpen: boolean;
  onClose: () => void;
}

const EnvironmentDebug: React.FC<EnvironmentDebugProps> = ({ isOpen, onClose }) => {
  const [copiedItems, setCopiedItems] = useState<Set<string>>(new Set());
  const [apiTestResults, setApiTestResults] = useState<Record<string, any>>({});

  // Get all environment variables
  const envVars = {
    'VITE_VIAPHOTON_API_URL': import.meta.env.VITE_VIAPHOTON_API_URL,
    'NODE_ENV': import.meta.env.NODE_ENV,
    'MODE': import.meta.env.MODE,
    'DEV': import.meta.env.DEV,
    'PROD': import.meta.env.PROD,
    'BASE_URL': import.meta.env.BASE_URL,
    'VITE_API_URL': import.meta.env.VITE_API_URL,
  };

  // Get API configuration
  const apiConfig = {
    'Current API Base URL': import.meta.env.VITE_VIAPHOTON_API_URL || '/api',
    'Fallback API URL': '/api',
    'Window Location': typeof window !== 'undefined' ? window.location.href : 'N/A',
    'Window Origin': typeof window !== 'undefined' ? window.location.origin : 'N/A',
    'User Agent': typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A',
  };

  // Test API endpoints
  const testApiEndpoint = async (endpoint: string) => {
    const baseUrl = import.meta.env.VITE_VIAPHOTON_API_URL || '/api';
    const url = `${baseUrl}${endpoint}`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');
      
      let data;
      if (isJson) {
        data = await response.json();
      } else {
        data = await response.text();
      }
      
      setApiTestResults(prev => ({
        ...prev,
        [endpoint]: {
          status: response.status,
          statusText: response.statusText,
          contentType,
          isJson,
          data: isJson ? data : data.substring(0, 200) + (data.length > 200 ? '...' : ''),
          success: response.ok,
          url
        }
      }));
    } catch (error) {
      setApiTestResults(prev => ({
        ...prev,
        [endpoint]: {
          error: error instanceof Error ? error.message : 'Unknown error',
          success: false,
          url
        }
      }));
    }
  };

  const copyToClipboard = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItems(prev => new Set([...prev, key]));
      setTimeout(() => {
        setCopiedItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(key);
          return newSet;
        });
      }, 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const testAllEndpoints = () => {
    const endpoints = [
      '/categories',
      '/tutorials',
      '/users',
      '/products',
      '/assignments'
    ];
    
    endpoints.forEach(endpoint => {
      testApiEndpoint(endpoint);
    });
  };

  useEffect(() => {
    if (isOpen) {
      testAllEndpoints();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Bug className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">Environment Debug</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Environment Variables */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Settings className="w-5 h-5 mr-2" />
              Environment Variables
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              {Object.entries(envVars).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between bg-white p-3 rounded border">
                  <div className="flex-1">
                    <div className="font-mono text-sm font-semibold text-gray-700">{key}</div>
                    <div className="font-mono text-sm text-gray-600 break-all">
                      {value || <span className="text-red-500 italic">undefined</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => copyToClipboard(value || '', key)}
                    className="ml-3 p-2 hover:bg-gray-100 rounded transition-colors"
                    title="Copy to clipboard"
                  >
                    {copiedItems.has(key) ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-500" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* API Configuration */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <ExternalLink className="w-5 h-5 mr-2" />
              API Configuration
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              {Object.entries(apiConfig).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between bg-white p-3 rounded border">
                  <div className="flex-1">
                    <div className="font-mono text-sm font-semibold text-gray-700">{key}</div>
                    <div className="font-mono text-sm text-gray-600 break-all">
                      {value || <span className="text-red-500 italic">undefined</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => copyToClipboard(value || '', key)}
                    className="ml-3 p-2 hover:bg-gray-100 rounded transition-colors"
                    title="Copy to clipboard"
                  >
                    {copiedItems.has(key) ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-500" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* API Test Results */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <ExternalLink className="w-5 h-5 mr-2" />
                API Test Results
              </h3>
              <button
                onClick={testAllEndpoints}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                Retest All
              </button>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              {Object.entries(apiTestResults).map(([endpoint, result]) => (
                <div key={endpoint} className="bg-white p-4 rounded border">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-mono text-sm font-semibold text-gray-700">
                      {endpoint}
                    </div>
                    <div className="flex items-center space-x-2">
                      {result.success ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-red-600" />
                      )}
                      <span className={`text-xs px-2 py-1 rounded ${
                        result.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {result.status || 'ERROR'}
                      </span>
                    </div>
                  </div>
                  <div className="text-xs text-gray-600 mb-2">
                    URL: <span className="font-mono">{result.url}</span>
                  </div>
                  {result.contentType && (
                    <div className="text-xs text-gray-600 mb-2">
                      Content-Type: <span className="font-mono">{result.contentType}</span>
                    </div>
                  )}
                  {result.error ? (
                    <div className="text-sm text-red-600 font-mono bg-red-50 p-2 rounded">
                      {result.error}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-700 font-mono bg-gray-50 p-2 rounded max-h-32 overflow-y-auto">
                      {typeof result.data === 'object' 
                        ? JSON.stringify(result.data, null, 2)
                        : result.data
                      }
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Local Storage */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Local Storage</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="bg-white p-3 rounded border">
                <div className="font-mono text-sm">
                  {typeof window !== 'undefined' ? (
                    <pre className="whitespace-pre-wrap text-xs">
                      {JSON.stringify(
                        Object.fromEntries(
                          Object.entries(localStorage).map(([key, value]) => [
                            key, 
                            key.includes('token') || key.includes('secret') 
                              ? value.substring(0, 10) + '...' 
                              : value
                          ])
                        ), 
                        null, 
                        2
                      )}
                    </pre>
                  ) : (
                    <span className="text-red-500">Not available</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default EnvironmentDebug;
